/* eslint-disable no-console */
import { db } from "@/firebase";
import encryptionService from "@/services/encryptionService";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from "firebase/firestore";

const state = {
  // Tier data
  tiers: [],
  allTierData: [],
  memberQualifiedTierData: [],
  companyQualifiedTierData: [],
  defaultMemberTier: [],
  defaultCompanyTier: [],
  currentTier: null,

  // Loading states
  loadingTiers: false,
  loadingAllTierData: false,
  loadingMemberQualifiedTierData: false,
  loadingCompanyQualifiedTierData: false,
  loadingCurrentTier: false,
  savingTier: false,

  // Error state
  error: null
};

const actions = {
  /**
   * Load all tiers for the program
   */
  async loadTiers({ commit, getters }) {
    commit("setLoadingTiers", true);
    commit("setTiersError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tiersRef = collection(programRef, "tiers");
      const tiersQuery = query(tiersRef, orderBy("tier", "asc"));
      const querySnapshot = await getDocs(tiersQuery);

      const tiers = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        tiers.push({
          id: docSnapshot.id,
          tier: data.tier,
          selectedParticipantType: data.selectedParticipantType,
          conditions: data.conditions || [],
          tierUppercase: data.tierUppercase,
          selectedDatabucket: data.selectedDatabucket,
          isDefault: data.isDefault,
          externalImageUrl: data.externalImageUrl || "",
          image: data.image || {
            name: "",
            updated: "",
            smallUrl: "",
            mediumUrl: "",
            largeUrl: "",
            smallStoragePath: "",
            mediumStoragePath: "",
            largeStoragePath: ""
          },
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      const defaultMemberTier = tiers
        .filter(el => el.isDefault === true)
        .filter(el => el.selectedParticipantType === "Member");
      commit("setDefaultMemberTier", defaultMemberTier);

      const defaultCompanyTier = tiers
        .filter(el => el.isDefault === true)
        .filter(el => el.selectedParticipantType === "Company");
      commit("setDefaultCompanyTier", defaultCompanyTier);

      commit("setTiers", tiers);
      return tiers;
    } catch (error) {
      console.error("Error loading tiers:", error);
      commit("setTiersError", error.message);
      commit("setTiers", []);
      throw error;
    } finally {
      commit("setLoadingTiers", false);
    }
  },

  /**
   * Get tier by ID
   */
  async getTierById({ commit, getters }, tierId) {
    if (!tierId) {
      console.error("Tier ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tierRef = doc(collection(programRef, "tiers"), tierId);
      const tierDoc = await getDoc(tierRef);

      if (tierDoc.exists()) {
        const data = tierDoc.data();
        return {
          id: tierDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Tier with ID ${tierId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting tier by ID:", error);
      commit("setTiersError", error.message);
      return null;
    }
  },

  /**
   * Load current tier details
   */
  async loadCurrentTier({ commit, dispatch }, tierId) {
    if (!tierId) {
      throw new Error("Tier ID is required");
    }

    commit("setLoadingCurrentTier", true);
    commit("setCurrentTier", null);
    commit("setTiersError", null);

    try {
      const tier = await dispatch("getTierById", tierId);
      if (tier) {
        commit("setCurrentTier", tier);
        return tier;
      } else {
        throw new Error("Tier not found");
      }
    } catch (error) {
      console.error("Error loading current tier:", error);
      commit("setTiersError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentTier", false);
    }
  },

  /**
   * Create a new tier
   */
  async createTier({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.tier) {
      throw new Error("Tier name is required");
    }

    commit("setSavingTier", true);
    commit("setTiersError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tiersRef = collection(programRef, "tiers");

      const tierUppercase = payload.tier.toUpperCase();

      // Check for duplicate tier names
      const duplicateQuery = query(
        tiersRef,
        where("tierUppercase", "==", tierUppercase)
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error("A tier with this name already exists");
      }

      const tier = {
        ...payload,
        tierUppercase,
        conditions: payload.conditions || [],
        isDefault: payload.isDefault || false,
        externalImageUrl: payload.externalImageUrl || "",
        image: payload.image || {
          name: "",
          updated: "",
          smallUrl: "",
          mediumUrl: "",
          largeUrl: "",
          smallStoragePath: "",
          mediumStoragePath: "",
          largeStoragePath: ""
        },
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system"
      };

      const newTierRef = await addDoc(tiersRef, tier);

      const tempTier = {
        ...tier,
        id: newTierRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createTier", tempTier);
      dispatch("setSnackbar", "Tier created successfully");

      return {
        success: true,
        tierId: newTierRef.id,
        tier: tempTier
      };
    } catch (error) {
      console.error("Error creating tier:", error);
      commit("setTiersError", error.message);
      if (error.message.includes("already exists")) {
        throw error;
      }
      throw new Error("Error when creating tier");
    } finally {
      commit("setSavingTier", false);
    }
  },

  /**
   * Update an existing tier
   */
  async updateTier({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.id) {
      throw new Error("Tier ID is required");
    }

    commit("setSavingTier", true);
    commit("setTiersError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tierRef = doc(collection(programRef, "tiers"), payload.id);

      // Verify tier exists
      const tierDoc = await getDoc(tierRef);
      if (!tierDoc.exists()) {
        throw new Error("Tier not found");
      }

      // Check for duplicate tier names (excluding current tier)
      if (payload.tier) {
        const tierUppercase = payload.tier.toUpperCase();
        const duplicateQuery = query(
          collection(programRef, "tiers"),
          where("tierUppercase", "==", tierUppercase)
        );
        const duplicateSnapshot = await getDocs(duplicateQuery);

        let duplicated = false;
        duplicateSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== payload.id) {
            duplicated = true;
          }
        });

        if (duplicated) {
          throw new Error("A tier with this name already exists");
        }
      }

      const updateData = {
        ...payload,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "system"
      };

      if (payload.tier) {
        updateData.tierUppercase = payload.tier.toUpperCase();
      }

      await updateDoc(tierRef, updateData);

      const updatedTier = {
        ...tierDoc.data(),
        ...payload,
        updated: new Date(),
        updatedBy: getters.user?.id || "system"
      };

      commit("updateTier", updatedTier);
      dispatch("setSnackbar", "Tier updated successfully");

      return updatedTier;
    } catch (error) {
      console.error("Error updating tier:", error);
      commit("setTiersError", error.message);
      if (error.message.includes("already exists")) {
        throw error;
      }
      throw new Error("Error when updating tier");
    } finally {
      commit("setSavingTier", false);
    }
  },

  /**
   * Delete a tier
   */
  async deleteTier({ commit, getters, dispatch }, tierId) {
    if (!tierId) {
      throw new Error("Tier ID is required");
    }

    commit("setTiersError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tierRef = doc(collection(programRef, "tiers"), tierId);

      await deleteDoc(tierRef);

      commit("deleteTier", tierId);
      dispatch("setSnackbar", "Tier deleted successfully");

      return true;
    } catch (error) {
      console.error("Error deleting tier:", error);
      commit("setTiersError", error.message);
      throw new Error("Error when deleting tier");
    }
  },

  /**
   * Load all tier data with databucket results
   */
  async loadAllTierData({ commit, getters }) {
    commit("setLoadingAllTierData", true);
    commit("setTiersError", null);
    commit("setAllTierData", []);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tiersRef = collection(programRef, "tiers");
      const tiersSnapshot = await getDocs(tiersRef);

      const databuckets = [];
      tiersSnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        if (data.selectedDatabucket) {
          databuckets.push({
            tierId: docSnapshot.id,
            id: data.selectedDatabucket,
            conditions: data.conditions,
            image: data.image,
            tier: data.tier,
            participantType: data.selectedParticipantType
          });
        }
      });

      let allTierData = [];

      for (const databucket of databuckets) {
        try {
          const databucketRef = doc(
            collection(programRef, "databuckets"),
            databucket.id
          );
          const totalResultRef = doc(
            collection(databucketRef, "results"),
            "total"
          );
          const totalsSnapshot = await getDoc(totalResultRef);

          let jsonString;
          if (totalsSnapshot.exists()) {
            jsonString = totalsSnapshot.data().csvData;
          } else {
            console.warn(`No data found for databucket ${databucket.id}`);
            continue;
          }

          const nestedArray = JSON.parse(jsonString || "[]");
          if (!nestedArray.length) continue;

          const labels = nestedArray[0];

          const camelLabels = labels.map(element => {
            const words = element.split(" ");
            const camelCaseElement = words
              .map((word, index) => {
                if (index === 0) {
                  return word.toLowerCase();
                }
                return word.charAt(0).toUpperCase() + word.slice(1);
              })
              .join("");
            return camelCaseElement;
          });

          const objectsArray = nestedArray.slice(1).map(row => {
            const object = {};
            camelLabels.forEach((label, index) => {
              object[label] = row[index];
            });
            object["databucketId"] = databucket.id;
            object["participantType"] = databucket.participantType;
            object["conditions"] = databucket.conditions;
            object["image"] = databucket.image;
            object["tier"] = databucket.tier;
            object["tierId"] = databucket.tierId;
            return object;
          });
          allTierData.push(...objectsArray);
        } catch (error) {
          console.error(`Error processing databucket ${databucket.id}:`, error);
          // Continue with other databuckets even if one fails
        }
      }

      commit("setAllTierData", allTierData);
      return allTierData;
    } catch (error) {
      console.error("Error loading all tier data:", error);
      commit("setTiersError", error.message);
      commit("setAllTierData", []);
      throw error;
    } finally {
      commit("setLoadingAllTierData", false);
    }
  },

  /**
   * Load member qualified tier data
   */
  async loadMemberQualifiedTierData({ dispatch, commit, getters }) {
    commit("setLoadingMemberQualifiedTierData", true);
    commit("setTiersError", null);

    try {
      await dispatch("loadAllTierData");
      const tierData = getters.allTierData.filter(
        el => el.participantType === "Member"
      );

      let results = [];

      tierData.forEach(tier => {
        let found = false;

        (tier.conditions || []).forEach(condition => {
          const targetVal = tier.target;
          const actualVal = tier.actual;
          const diffVal = tier.difference;
          const percentVal = tier.percentage;
          const pointsVal = tier.targetPoints;
          const claimPointsVal = tier.claimPoints;
          const quizPointsVal = tier.quizPoints;
          const totalPointsVal = tier.totalPoints;

          const realStatement = condition.statement
            .replace(/__actual/gi, actualVal)
            .replace(/__target/gi, targetVal)
            .replace(/__percent/gi, percentVal)
            .replace(/__diff/gi, diffVal)
            .replace(/__points/gi, pointsVal)
            .replace(/__offerPoints/gi, claimPointsVal)
            .replace(/__quizPoints/gi, quizPointsVal)
            .replace(/__totalPoints/gi, totalPointsVal);

          try {
            const qualified = Function("return " + realStatement)();
            if (qualified === true) {
              found = true;
              return;
            }
          } catch (error) {
            console.error("Error evaluating tier condition:", error);
          }
        });

        results.push({
          ...tier,
          qualified: found
        });
      });

      const matchedMemberArray = [];
      const currentUser = getters.currentUser;

      if (currentUser && currentUser.accountKey) {
        results
          .filter(el => el.qualified === true)
          .forEach(tier => {
            const accountKeyMatches = currentUser.accountKey === tier.accountId;

            if (accountKeyMatches) {
              const matchedItem = {
                ...tier,
                fullname: `${currentUser.firstname || ""} ${currentUser.lastname || ""}`.trim(),
                email: currentUser.email,
                memberId: currentUser._id
              };
              matchedMemberArray.push(matchedItem);
            }
          });
      }

      commit("setMemberQualifiedTierData", matchedMemberArray);
      return matchedMemberArray;
    } catch (error) {
      console.error("Error loading member qualified tier data:", error);
      commit("setTiersError", error.message);
      commit("setMemberQualifiedTierData", []);
      throw error;
    } finally {
      commit("setLoadingMemberQualifiedTierData", false);
    }
  },

  /**
   * Load company qualified tier data
   */
  async loadCompanyQualifiedTierData({ dispatch, commit, getters }) {
    commit("setLoadingCompanyQualifiedTierData", true);
    commit("setTiersError", null);

    try {
      await dispatch("loadAllTierData");
      const tierData = getters.allTierData.filter(
        el => el.participantType === "Company"
      );
      let results = [];

      tierData.forEach(tier => {
        let found = false;

        (tier.conditions || []).forEach(condition => {
          const targetVal = tier.target;
          const actualVal = tier.actual;
          const diffVal = tier.difference;
          const percentVal = tier.percentage;
          const pointsVal = tier.targetPoints;
          const claimPointsVal = tier.claimPoints;
          const quizPointsVal = tier.quizPoints;
          const totalPointsVal = tier.totalPoints;

          const realStatement = condition.statement
            .replace(/__actual/gi, actualVal)
            .replace(/__target/gi, targetVal)
            .replace(/__percent/gi, percentVal)
            .replace(/__diff/gi, diffVal)
            .replace(/__points/gi, pointsVal)
            .replace(/__offerPoints/gi, claimPointsVal)
            .replace(/__quizPoints/gi, quizPointsVal)
            .replace(/__totalPoints/gi, totalPointsVal);

          try {
            const qualified = Function("return " + realStatement)();
            if (qualified === true) {
              found = true;
              return;
            }
          } catch (error) {
            console.error("Error evaluating tier condition:", error);
          }
        });

        results.push({
          ...tier,
          qualified: found
        });
      });

      const matchedCompanyArray = [];
      await dispatch("loadCompanies");
      results
        .filter(el => el.qualified === true)
        .forEach(tier => {
          const matchingCompany = getters.companies.find(
            company => company.accountKey === tier.accountId
          );
          if (matchingCompany) {
            const matchedItem = {
              ...tier,
              title: matchingCompany.title,
              companyId: matchingCompany.id,
              companyMembers: matchingCompany.members
            };
            matchedCompanyArray.push(matchedItem);
          }
        });

      commit("setCompanyQualifiedTierData", matchedCompanyArray);
      return matchedCompanyArray;
    } catch (error) {
      console.error("Error loading company qualified tier data:", error);
      commit("setTiersError", error.message);
      commit("setCompanyQualifiedTierData", []);
      throw error;
    } finally {
      commit("setLoadingCompanyQualifiedTierData", false);
    }
  },

  /**
   * Check if participant qualifies for tier
   */
  checkQualified(context, payload) {
    if (!payload || !payload.conditions) {
      return false;
    }

    let found = false;
    const conditions = payload.conditions;

    conditions.forEach(condition => {
      const targetVal = payload.target;
      const actualVal = payload.actual;
      const diffVal = payload.difference;
      const percentVal = payload.percentage;
      const pointsVal = payload.targetPoints;
      const claimPointsVal = payload.claimPoints;
      const quizPointsVal = payload.quizPoints;
      const totalPointsVal = payload.totalPoints;

      const realStatement = condition.statement
        .replace(/__actual/gi, actualVal)
        .replace(/__target/gi, targetVal)
        .replace(/__percent/gi, percentVal)
        .replace(/__diff/gi, diffVal)
        .replace(/__points/gi, pointsVal)
        .replace(/__offerPoints/gi, claimPointsVal)
        .replace(/__quizPoints/gi, quizPointsVal)
        .replace(/__totalPoints/gi, totalPointsVal);

      try {
        const qualified = Function("return " + realStatement)();
        if (qualified === true) {
          found = true;
          return;
        }
      } catch (error) {
        console.error("Error evaluating tier condition:", error);
      }
    });

    return found;
  },

  /**
   * Load tiers by participant type
   */
  async loadTiersByParticipantType({ commit, getters }, participantType) {
    if (!participantType) {
      throw new Error("Participant type is required");
    }

    commit("setLoadingTiers", true);
    commit("setTiersError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tiersRef = collection(programRef, "tiers");
      const participantQuery = query(
        tiersRef,
        where("selectedParticipantType", "==", participantType),
        orderBy("tier", "asc")
      );
      const querySnapshot = await getDocs(participantQuery);

      const tiers = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        tiers.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      return tiers;
    } catch (error) {
      console.error("Error loading tiers by participant type:", error);
      commit("setTiersError", error.message);
      throw error;
    } finally {
      commit("setLoadingTiers", false);
    }
  },

  /**
   * Set default tier for participant type
   */
  async setDefaultTier(
    { commit, getters, dispatch },
    { tierId, participantType }
  ) {
    if (!tierId || !participantType) {
      throw new Error("Tier ID and participant type are required");
    }

    commit("setSavingTier", true);
    commit("setTiersError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tiersRef = collection(programRef, "tiers");

      // First, remove default status from all tiers of this participant type
      const existingDefaultQuery = query(
        tiersRef,
        where("selectedParticipantType", "==", participantType),
        where("isDefault", "==", true)
      );
      const existingDefaultSnapshot = await getDocs(existingDefaultQuery);

      const updatePromises = [];
      existingDefaultSnapshot.forEach(docSnapshot => {
        const tierRef = doc(tiersRef, docSnapshot.id);
        updatePromises.push(updateDoc(tierRef, { isDefault: false }));
      });

      // Set the new default tier
      const newDefaultTierRef = doc(tiersRef, tierId);
      updatePromises.push(updateDoc(newDefaultTierRef, { isDefault: true }));

      await Promise.all(updatePromises);

      // Reload tiers to reflect changes
      await dispatch("loadTiers");
      dispatch("setSnackbar", "Default tier updated successfully");

      return true;
    } catch (error) {
      console.error("Error setting default tier:", error);
      commit("setTiersError", error.message);
      throw new Error("Error when setting default tier");
    } finally {
      commit("setSavingTier", false);
    }
  },

  // State management actions
  clearTiersError({ commit }) {
    commit("setTiersError", null);
  },

  clearCurrentTier({ commit }) {
    commit("setCurrentTier", null);
  },

  clearAllTierData({ commit }) {
    commit("setAllTierData", []);
    commit("setMemberQualifiedTierData", []);
    commit("setCompanyQualifiedTierData", []);
  }
};

const mutations = {
  setTiers(state, payload) {
    state.tiers = payload;
  },

  setAllTierData(state, payload) {
    state.allTierData = payload;
  },

  setMemberQualifiedTierData(state, payload) {
    state.memberQualifiedTierData = payload;
  },

  setCompanyQualifiedTierData(state, payload) {
    state.companyQualifiedTierData = payload;
  },

  setDefaultMemberTier(state, payload) {
    state.defaultMemberTier = payload;
  },

  setDefaultCompanyTier(state, payload) {
    state.defaultCompanyTier = payload;
  },

  setCurrentTier(state, payload) {
    state.currentTier = payload;
  },

  setLoadingTiers(state, payload) {
    state.loadingTiers = Boolean(payload);
  },

  setLoadingAllTierData(state, payload) {
    state.loadingAllTierData = Boolean(payload);
  },

  setLoadingMemberQualifiedTierData(state, payload) {
    state.loadingMemberQualifiedTierData = Boolean(payload);
  },

  setLoadingCompanyQualifiedTierData(state, payload) {
    state.loadingCompanyQualifiedTierData = Boolean(payload);
  },

  setLoadingCurrentTier(state, payload) {
    state.loadingCurrentTier = Boolean(payload);
  },

  setSavingTier(state, payload) {
    state.savingTier = Boolean(payload);
  },

  setTiersError(state, payload) {
    state.error = payload;
  },

  createTier(state, payload) {
    state.tiers = [...state.tiers, payload];
  },

  updateTier(state, payload) {
    state.tiers = state.tiers.map(item => {
      if (item.id === payload.id) {
        return { ...item, ...payload };
      }
      return item;
    });

    // Update current tier if it matches
    if (state.currentTier && state.currentTier.id === payload.id) {
      state.currentTier = { ...state.currentTier, ...payload };
    }

    // Update default tiers if necessary
    if (payload.isDefault && payload.selectedParticipantType === "Member") {
      state.defaultMemberTier = [payload];
    }
    if (payload.isDefault && payload.selectedParticipantType === "Company") {
      state.defaultCompanyTier = [payload];
    }
  },

  deleteTier(state, tierId) {
    state.tiers = state.tiers.filter(item => item.id !== tierId);

    // Clear current tier if it matches
    if (state.currentTier && state.currentTier.id === tierId) {
      state.currentTier = null;
    }

    // Update default tiers
    state.defaultMemberTier = state.defaultMemberTier.filter(
      item => item.id !== tierId
    );
    state.defaultCompanyTier = state.defaultCompanyTier.filter(
      item => item.id !== tierId
    );
  }
};

const getters = {
  /**
   * Get all tiers
   */
  tiers(state) {
    return state.tiers;
  },

  /**
   * Get all tier data with databucket results
   */
  allTierData(state) {
    return state.allTierData;
  },

  /**
   * Get member qualified tier data
   */
  memberQualifiedTierData(state) {
    return state.memberQualifiedTierData;
  },

  /**
   * Get company qualified tier data
   */
  companyQualifiedTierData(state) {
    return state.companyQualifiedTierData;
  },

  /**
   * Get default member tier
   */
  defaultMemberTier(state) {
    return state.defaultMemberTier;
  },

  /**
   * Get default company tier
   */
  defaultCompanyTier(state) {
    return state.defaultCompanyTier;
  },

  /**
   * Get current tier
   */
  currentTier(state) {
    return state.currentTier;
  },

  /**
   * Get tier by ID
   */
  getTierById: state => id => {
    return state.tiers.find(tier => tier.id === id) || null;
  },

  /**
   * Get tiers by participant type
   */
  getTiersByParticipantType: state => participantType => {
    return state.tiers.filter(
      tier => tier.selectedParticipantType === participantType
    );
  },

  /**
   * Get member tiers
   */
  memberTiers(state) {
    return state.tiers.filter(
      tier => tier.selectedParticipantType === "Member"
    );
  },

  /**
   * Get company tiers
   */
  companyTiers(state) {
    return state.tiers.filter(
      tier => tier.selectedParticipantType === "Company"
    );
  },

  /**
   * Get default tiers
   */
  defaultTiers(state) {
    return state.tiers.filter(tier => tier.isDefault === true);
  },

  /**
   * Get tiers with databuckets
   */
  tiersWithDatabuckets(state) {
    return state.tiers.filter(tier => tier.selectedDatabucket);
  },

  /**
   * Get loading states
   */
  loadingTiers(state) {
    return state.loadingTiers;
  },

  loadingAllTierData(state) {
    return state.loadingAllTierData;
  },

  loadingMemberQualifiedTierData(state) {
    return state.loadingMemberQualifiedTierData;
  },

  loadingCompanyQualifiedTierData(state) {
    return state.loadingCompanyQualifiedTierData;
  },

  loadingCurrentTier(state) {
    return state.loadingCurrentTier;
  },

  savingTier(state) {
    return state.savingTier;
  },

  /**
   * Get error state
   */
  tiersError(state) {
    return state.error;
  },

  /**
   * Check if any tier operations are loading
   */
  tiersIsLoading(state) {
    return (
      state.loadingTiers ||
      state.loadingAllTierData ||
      state.loadingMemberQualifiedTierData ||
      state.loadingCompanyQualifiedTierData ||
      state.loadingCurrentTier ||
      state.savingTier
    );
  },

  /**
   * Get tiers count
   */
  tiersCount(state) {
    return state.tiers.length;
  },

  /**
   * Get member tiers count
   */
  memberTiersCount(state) {
    return state.tiers.filter(tier => tier.selectedParticipantType === "Member")
      .length;
  },

  /**
   * Get company tiers count
   */
  companyTiersCount(state) {
    return state.tiers.filter(
      tier => tier.selectedParticipantType === "Company"
    ).length;
  },

  /**
   * Get qualified members count
   */
  qualifiedMembersCount(state) {
    return state.memberQualifiedTierData.length;
  },

  /**
   * Get qualified companies count
   */
  qualifiedCompaniesCount(state) {
    return state.companyQualifiedTierData.length;
  },

  /**
   * Check if tiers exist
   */
  hasTiers(state) {
    return state.tiers.length > 0;
  },

  /**
   * Check if default member tier exists
   */
  hasDefaultMemberTier(state) {
    return state.defaultMemberTier.length > 0;
  },

  /**
   * Check if default company tier exists
   */
  hasDefaultCompanyTier(state) {
    return state.defaultCompanyTier.length > 0;
  },

  /**
   * Get tiers as options for dropdowns
   */
  tiersOptions(state) {
    return state.tiers.map(tier => ({
      text: tier.tier,
      value: tier.id
    }));
  },

  /**
   * Get member tiers as options
   */
  memberTiersOptions(state) {
    return state.tiers
      .filter(tier => tier.selectedParticipantType === "Member")
      .map(tier => ({
        text: tier.tier,
        value: tier.id
      }));
  },

  /**
   * Get company tiers as options
   */
  companyTiersOptions(state) {
    return state.tiers
      .filter(tier => tier.selectedParticipantType === "Company")
      .map(tier => ({
        text: tier.tier,
        value: tier.id
      }));
  },

  /**
   * Search tiers by name
   */
  searchTiers: state => searchTerm => {
    if (!searchTerm) return state.tiers;

    const term = searchTerm.toLowerCase();
    return state.tiers.filter(tier => tier.tier.toLowerCase().includes(term));
  },

  /**
   * Get tier statistics
   */
  tierStatistics(state) {
    const total = state.tiers.length;
    const memberTiers = state.tiers.filter(
      tier => tier.selectedParticipantType === "Member"
    ).length;
    const companyTiers = state.tiers.filter(
      tier => tier.selectedParticipantType === "Company"
    ).length;
    const defaultTiers = state.tiers.filter(tier => tier.isDefault === true)
      .length;
    const tiersWithDatabuckets = state.tiers.filter(
      tier => tier.selectedDatabucket
    ).length;
    const qualifiedMembers = state.memberQualifiedTierData.length;
    const qualifiedCompanies = state.companyQualifiedTierData.length;

    return {
      total,
      memberTiers,
      companyTiers,
      defaultTiers,
      tiersWithDatabuckets,
      qualifiedMembers,
      qualifiedCompanies
    };
  },

  /**
   * Get recent tiers (last 10)
   */
  recentTiers(state) {
    return [...state.tiers]
      .sort((a, b) => new Date(b.updated) - new Date(a.updated))
      .slice(0, 10);
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
